<script lang="ts" setup>
  import { useHttpClient, useConfirmation, useRoute, useTranslator, useShared } from '@othercommerce/foundation';
  import { ref, type PropType } from 'vue';
  import { useBreakpoints, useGoogleTagManager, addWishlistItemsToCartEvent } from '@storefront/storefront';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import type { Wishlist } from '@storefront/types';

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  const props = defineProps({
    wishlist: { type: Object as PropType<Wishlist>, required: true },
    products: { type: Object, required: true },
  });
  // @Todo We should get the `isNew` property as boolean, not as number in products.

  const confirm = useConfirmation();
  const breakpoints = useBreakpoints();
  const open = ref(false);
  const http = useHttpClient();
  const route = useRoute();
  const shared = useShared();
  const translator = useTranslator();

  function onDestroy(wishlist: Wishlist, variant: number) {
    confirm({
      callback: () => http.delete(route('web.account.wishlist.variant.destroy', { wishlist, variant })),
      title: translator.trans('SHOP.ACCOUNT.HEADER.WISHLIST_PRODUCT_DESTROY'),
      description: translator.trans('SHOP.ACCOUNT.MESSAGE.WISHLIST_PRODUCT_DESTROY'),
      yes: translator.trans('SHOP.ACTION.DESTROY'),
      no: translator.trans('SHOP.ACTION.CANCEL'),
    }).catch(() => void (0));
  }

  const selected = ref<number[]>([]);

  const gtm = useGoogleTagManager();

  function toggleSelected(variantId: number) {
    const index = selected.value.indexOf(variantId);

    if (index === -1) {
      selected.value.push(variantId);
    } else {
      selected.value.splice(index, 1);
    }
  }

  function onSelectedAdd(wishlist: Wishlist, selected: number[] | null) {
    if (selected === null) {
      gtm.push(addWishlistItemsToCartEvent(props.products.data, shared.value.activeCurrencyCode));

      return http.post(route('web.account.wishlist.cart.store', { wishlist }));
    }

    const items = props.products.data.filter((product: { variantId: number }) => {
      return selected?.includes(product.variantId);
    });

    gtm.push(addWishlistItemsToCartEvent(items, shared.value.activeCurrencyCode));

    return http.post(route('web.account.wishlist.cart.selected.store', { wishlist, selected }));
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ wishlist.name }}
  </AccountMobileNavigation>
  <SfTile class="account-wishlist-record" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ wishlist.name }}
      </h1>
    </template>
    <template #default>
      <div v-if="products.data.length > 0" class="account-wishlist-record__items">
        <ProductCard
          v-for="product in products.data"
          :key="product.id"
          :horizontal="breakpoints.md"
          :product="product"
          :selected="selected.includes(product.variantId)"
          selectable
          @toggleSelected="toggleSelected(product.variantId)">
          <template #icons>
            <SfButton :aria-label="$t('SHOP.ACTION.DESTROY')" danger rounded square transparent @click.prevent.stop="onDestroy(wishlist, product.variantId)">
              <template #before>
                <SfIconTrash aria-hidden="true" />
              </template>
            </SfButton>
          </template>
        </ProductCard>
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.WISHLIST_PRODUCTS_EMPTY') }}
      </SfDataPlaceholder>
    </template>
    <template #footer>
      <div class="account-wishlist-record__footer">
        <SfButton :href="$route('web.account.wishlist.index')">
          <template #before>
            <SfIconChevronLeft />
          </template>
          <template #default>
            {{ $t('SHOP.ACCOUNT.ACTION.WISHLIST_BACK') }}
          </template>
        </SfButton>
        <SfButton primary @click="open = true">
          <template #before>
            <SfIconPen aria-hidden="true" />
          </template>
          <template #default>
            {{ $t('SHOP.ACTION.EDIT') }}
          </template>
        </SfButton>
        <SfButton primary @click="onSelectedAdd(wishlist, null)">
          <template #before>
            <SfIconCartAdd aria-hidden="true" />
          </template>
          <template #default>
            {{ $t('SHOP.ACCOUNT.ACTION.WISHLIST_ADD_TO_CART') }}
          </template>
        </SfButton>
        <SfButton :disabled="selected.length === 0" primary @click="onSelectedAdd(wishlist, selected)">
          <template #before>
            <SfIconCartAdd aria-hidden="true" />
          </template>
          <template #default>
            {{ $t('SHOP.ACCOUNT.ACTION.WISHLIST_ADD_SELECTED_TO_CART') }}
          </template>
        </SfButton>
      </div>
    </template>
  </SfTile>
  <SfPagination :pagination="products" />
  <WishlistEditModal v-model:open="open" :wishlist="wishlist" @saved="open = false" />

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-wishlist-record {
    &__items {
      display: flex;
      flex-direction: column;
      row-gap: 1rem;
    }

    &__footer {
      display: flex;
      gap: 0.5rem;
    }
  }

  @include media.query(sm) {
    .account-wishlist-record .tile__footer {
      flex-wrap: wrap;

      .btn {
        flex-grow: 1;
      }
    }
  }

  @include media.query(xxl) {
    .account-wishlist-record {
      &__footer {
        flex-direction: column;
      }
    }
  }
</style>
